<template>
  <div class="results">
    <section class="headtop">
      <div class="container">
        <div class="col-sm-9 right-border">
          <h1>Results</h1>
          <br>
          <RunButtons :key="'runbuttons_' + currentStep" />
        </div>
        <div class="col-sm-3">
          <CovidStatus :key="'covidstatus_' + currentStep" />
        </div>
        <div class="clearfix" />
      </div>
    </section>

    <div class="row headtext">
      <div class="container">
        <div class="col-sm-12">
          The results of MCSim are not to be treated as forecasts; you will find that a range of results can be obtained, depending on your choices and the decisions you make throughout the simulation. The comparison of  outputs should therefore rather be thought as a way of rehearsing different scenarios and searching for the most effective ways of enhancing the region.
        </div>
        <br><br><br><br>
        <div class="col-md-9 column">
          <SavedRuns @load-run-to-compare="loadRunToCompare" />
        </div>
      </div>
    </div>

    <div class="container">
      <div class="clearfix">
        <div class="col-xs-10">
          <ul class="nav nav-pills results-top">
            <template v-for="(zone, index) in zones">
              <li
                :key="index"
                role="presentation"
                :class="chosenOutputZone == index ? 'active' : ''"
              >
                <a @click="displayCurrentValues(index)">{{ zone.label }}</a>
              </li>
            </template>
          </ul>
        </div>
        <div class="col-xs-2 text-right help-button-container">
          <a
            href
            class="help-button"
            @click.prevent="displayModal('help')"
          >
            <small>
              <span class="badge badge-info">?</span>
              <strong>Help</strong>
            </small>
          </a>
        </div>
      </div>

      <div class="col-xs-12">
        <br>
        <h3>Impact on key factors</h3>
        <br>
      </div>

      <div class="row">
        <div class="col-xs-12">
          <template v-for="cat in outputCategories">
            <div :key="'outputcat_' + cat.reference">
              <div class="col-xs-12">
                <h4>{{ cat.label }}</h4>
                <br>
              </div>
              <div>
                <template v-for="output in outputsForCategory(cat.reference)">
                  <div
                    :key="currentStep + '_' + output.name"
                    class="col-xs-4"
                  >
                    <section class="widget kpi-container">
                      <h4>
                        {{ output.label }}
                        <span
                          class="info-badge badge hidden"
                          data-toggle="popover"
                          data-trigger="hover"
                          data-container="body"
                          data-placement="right"
                          :data-title="output.label"
                          :data-content="output.description"
                        >i</span>
                      </h4>
                      <div
                        :class="
                          'kpi-value kpi-values-' +
                            output.formattedValues.length
                        "
                      >
                        <template v-if="output.formattedValues.length > 1">
                          <table class="table table-striped">
                            <template v-for="(o, vIndex) in output.formattedValues">
                              <tr
                                :key="
                                  currentStep + '_' + output.name + '_' + vIndex
                                "
                              >
                                <td>{{ o.label }}</td>
                                <td>{{ o.value }}</td>
                                <td class="kpi-baseline">
                                  {{ o.formattedBaselineText }}
                                </td>
                              </tr>
                            </template>
                          </table>
                        </template>
                        <template v-else>
                          {{ output.formattedValues[0].value }}
                        </template>
                      </div>
                      <template v-if="output.formattedValues.length === 1">
                        <p class="kpi-baseline">
                          {{ output.formattedValues[0].formattedBaselineText }}
                        </p>
                      </template>
                      <div class="kpi-footer">
                        <a
                          class="btn btn-xs"
                          @click="viewKPIDetails(output)"
                        >
                          <img
                            src="/static/img/udm/icon-graph.png"
                            class="charts-icon"
                          >
                          View graph
                        </a>
                      </div>
                    </section>
                  </div>
                </template>

                <div class="clearfix" />

                <br>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <KPIDetailsModalComponent
      :key="currentStep + ',' + chosenOutputZone + ',' + graphBaselineName"
      :output="detailsOutput"
      :chosen-output-zone="chosenOutputZone"
      :baseline-output="graphBaseline"
      :baseline-name="graphBaselineName"
      :saved-run-name="currentRun.getName()"
    />
    <HelpModalComponent />
  </div>
</template>

<script>
import HelpModalComponent from "../components/modals/Help";
import KPIDetailsModalComponent from "../components/modals/KPIDetails";
import CovidStatus from "../components/CovidStatus";
import SavedRuns from "../components/SavedRuns";
import RunButtons from "../components/RunButtons";

import data from "../services/data";
import baselines from "../storage/data/baselines";
import numeral from "numeral";
import Run from "../storage/Run";

export default {
  components: {
    KPIDetailsModalComponent,
    CovidStatus,
    HelpModalComponent,
    RunButtons,
    SavedRuns,
  },
  data() {
    return {
      currentRun: this.$root.$data.sharedStorage.getCurrentRun(),
      currentStep: 0,
      savedRuns: this.$root.$data.sharedStorage.getSavedRuns(),
      zones: data.subscriptOptions.zones,
      outputCategories: data.outputCategories,
      debug: "",
      kpiBoxOutputs: [],
      detailsOutput: {},
      chosenOutputZone: 0,
      mapPopupData: {},
      graphBaseline: null,
      graphBaselineName: "",
    };
  },
  created() {
    window.scrollTo(0, 0);
    this.$root.$data.eventHub.$on("new-data-arrived", () => {
      this.currentRun = this.$root.$data.sharedStorage.getCurrentRun();
      this.currentStep = this.currentRun.getCurrentStep();
      this.calculateKpiBoxOutputs();
      this.displayCurrentValues();
    });

    const slug = this.currentRun.getComparisionSlug();
    if (slug) {
      this.loadRunToCompare(slug);
    } else {
      let index = 0;
      this.loadRunToCompare(`scenario_${index}`);
    }
  },
  mounted() {
    this.calculateKpiBoxOutputs();
    this.displayCurrentValues();
    this.currentRun = this.$root.$data.sharedStorage.getCurrentRun();
    this.currentStep = this.currentRun.getCurrentStep();
  },
  methods: {
    setBaseline(newBaselineData) {
      this.currentRun = this.$root.$data.sharedStorage.getCurrentRun();
      if (this.graphBaseline && this.graphBaseline !== {} && !newBaselineData) {
        return;
      }

      if (newBaselineData) {
        this.graphBaseline = newBaselineData;
        return;
      }

      const scenario = this.currentRun.getScenario();

      this.graphBaseline =
        scenario && scenario.baselineId ? baselines[scenario.baselineId] : {};
    },
    /**
     * Helper function to get formatted values and baseline text
     * for one output value
     */
    getFormattedValueForOneZone(format, value, baselineValue) {
      const index = value.length - 1;
      value = value[index];
      baselineValue = baselineValue[index];

      const formattedValue = numeral(value).format(format);
      const formattedBaselineValue = numeral(baselineValue).format(format);

      let formattedBaselineText;

      // Small change is 0.1% of the lower value
      const smallChange =
        Math.abs(value - baselineValue) <
        Math.abs(Math.min(value, baselineValue)) * 0.001;

      if (smallChange) {
        formattedBaselineText = `No change over ${this.graphBaselineName}`;
      } else {
        if (baselineValue > value) {
          if (format.indexOf("%") >= 0) {
            formattedBaselineText = `${numeral(
              (baselineValue - value) * 100
            ).format("0.0")} points lower than ${this.graphBaselineName}`;
          } else {
            formattedBaselineText = `${numeral(baselineValue - value).format(
              format
            )} lower than ${this.graphBaselineName}`;
          }
        } else {
          if (format.indexOf("%") >= 0) {
            formattedBaselineText = `${numeral(
              (value - baselineValue) * 100
            ).format("0.0")} points higher than ${this.graphBaselineName}`;
          } else {
            formattedBaselineText = `${numeral(value - baselineValue).format(
              format
            )} higher than ${this.graphBaselineName}`;
          }
        }
      }

      return {
        value: formattedValue,
        baselineValue: formattedBaselineValue,
        formattedBaselineText,
      };
    },
    getFormattedValuesForOutput(output, values, baselineValues) {
      const zone = data.subscriptOptions.zones[this.chosenOutputZone].name;

      let formattedValues = [];

      if (output.subscripts && output.subscripts[0] !== "zones") {
        for (let v of Object.keys(values)) {
          const lastValue = values[v][zone];
          const baselineValue = baselineValues[v][zone];

          const formattedValue = this.getFormattedValueForOneZone(
            output.format,
            lastValue,
            baselineValue
          );

          formattedValues.push({
            label: v,
            value: formattedValue.value,
            baselineValue: formattedValue.baselineValue,
            formattedBaselineText: formattedValue.formattedBaselineText,
          });
        }
      } else {
        const lastValue = values[zone];
        const baselineValue = baselineValues[zone];

        const formattedValue = this.getFormattedValueForOneZone(
          output.format,
          lastValue,
          baselineValue
        );

        formattedValues.push({
          label: "",
          value: formattedValue.value,
          baselineValue: formattedValue.baselineValue,
          formattedBaselineText: formattedValue.formattedBaselineText,
        });
      }

      return {
        name: output.name,
        category: output.category,
        subscripts: output.subscripts,
        format: output.format,
        label: output.label,
        formattedValues,
      };
    },
    calculateKpiBoxOutputs() {
      this.currentRun = this.$root.$data.sharedStorage.getCurrentRun();
      const savedOutput = this.currentRun.getSavedOutput();
      if (!savedOutput) {
        return;
      }
      this.kpiBoxOutputs = [];
      this.setBaseline();

      for (let output of data.outputs) {
        if (!output.display_in_results) {
          continue;
        }

        const values = savedOutput[output.name];
        if (!values) {
          continue;
        }

        const scenario = this.currentRun.getScenario();
        const baselineValues =
          scenario && scenario.baselineId ? baselines[scenario.baselineId] : {};
        const baselineValuesForOutput = baselineValues[output.name];

        const obj = this.getFormattedValuesForOutput(
          output,
          values,
          baselineValuesForOutput
        );

        this.kpiBoxOutputs.push(obj);
      }
    },
    displayCurrentValues(zoneIndex) {
      this.chosenOutputZone = zoneIndex !== undefined ? zoneIndex : 0;

      const savedOutput = this.currentRun.getSavedOutput();
      if (!savedOutput) {
        return;
      }

      this.debug = savedOutput;
      this.calculateKpiBoxOutputs();
    },
    viewKPIDetails(output) {
      const savedOutput = this.currentRun.getSavedOutput();
      if (!savedOutput) {
        alert("There are no details to show");
        return;
      }
      this.detailsOutput = output;
      var _this = this;
      setTimeout(function () {
        _this.$root.$data.eventHub.$emit("show-kpi-modal");
      }, 200);
    },
    displayModal(modalName) {
      this.$root.$data.eventHub.$emit("show-" + modalName + "-modal");
    },
    loadRunToCompare(selectedRunSlug) {
      if (!selectedRunSlug || selectedRunSlug.indexOf("_") === -1) {
        return;
      }

      this.currentRun.setComparisionSlug(selectedRunSlug);

      const source = selectedRunSlug.split("_")[0];
      const index = selectedRunSlug.split("_")[1];

      if (source === "scenario") {
        this.graphBaselineName = data.scenarios[index].title;
        this.graphBaseline = baselines[data.scenarios[index].baselineId] || {};
      }

      if (source === "savedrun") {
        const savedRun = this.savedRuns[index];
        const run = new Run();
        run.addDataFromJson(savedRun);
        this.graphBaselineName = run.getName();
        this.graphBaseline = run.getSavedOutput() || {};
      }

      this.calculateKpiBoxOutputs();
      this.displayCurrentValues();
    },
    /**
     * For display only: we display outputs grouped by their category.
     * This is a helper function to return all outputs that belong to
     * a specific group.
     */
    outputsForCategory(categoryName) {
      if (!categoryName) {
        return this.kpiBoxOutputs;
      }
      return this.kpiBoxOutputs.filter((e) => e.category === categoryName);
    },
  },
};
</script>

<style lang="scss">
.mapComponentWidget {
  padding: 0;
}
#mapViewContainer {
  width: 500px;
  margin: 0 auto;
}
.focus-output .column .widget {
  height: 320px;
}
.kpi-container {
  position: relative;
  height: 195px;
  overflow: hidden;
  padding: 15px;
}
.kpi-container h4 {
  font-size: 16px;
  font-weight: 100;
  margin: 8px 0 4px 0;
}
.kpi-value {
  font-size: 50px;
  color: #009482;
  margin: 0;
}
.kpi-values-3,
.kpi-values-5 {
  font-size: 12px;
}
.kpi-baseline {
  margin: 0 0 15px 0;
  font-size: 0.8em;
  color: black;
}
.kpi-footer {
  background-color: #e6e5e5;
  padding: 8px 6px;
  position: absolute;
  bottom: 0px;
  right: 0px;
  left: 0px;
}
.kpi-footer a {
  color: #57626e;
  font-size: 0.8em;
  width: 100%;
  text-align: left;
}
.kpi-footer .charts-icon {
  width: 15px;
  height: 10px;
  margin: -2px 5px 0 5px;
}

.buttons {
  margin: 15px 0 10px 0;
}
.results-header-links,
.results-header-links:hover {
  color: #fff;
  font-size: 0.8em;
}
.help-button-container {
  padding-top: 20px;
}
.help-button b {
  font-weight: 400;
}
.help-button .badge {
  margin: -1px 3px 1px 0;
  background-color: #009482;
  font-size: 10px;
  width: 16px;
  height: 16px;
  padding: 3px 0 0 1px;
}
.nav-pills {
  a {
    cursor: pointer;
  }
  &.results-top {
    font-size: 1.25em;
  }
}
</style>
